import React, { useState, useEffect } from 'react';

/**
 * SAT용 기본 페이지네이션
 * @param {number} pageIndex 부모와의 pageIndex Sync
 * @param {number} totalCount 전체 항목의 개수
 * @param {number} viewItemCount 한 페이지에 노출 될 항목들의 개수
 * @param {number} viewPageCount 한번에 볼 페이지들의 개수
 * @param {array} prevButtons 이전 페이지 버튼들 ['all', 'list', 'page']
 * @param {array} nextButtons 다음 페이지 버튼들 ['all', 'list', 'page']
 * @param {function} onChange pageIndex가 변경 될 때에 동작하는 함수
 * @returns {ReactComponentElement} pagination 컴포넌트
 */
const SatPagination = ({
  pageIndex,
  totalCount,
  viewItemCount,
  viewPageCount,
  prevButtons,
  nextButtons,
  onChange,
  initial = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(pageIndex);
  const [indicatorIndex, setIndicatorIndex] = useState(
    $utils.getIndicatorIndex(pageIndex, viewPageCount),
  );

  const startPageIndex = indicatorIndex * viewItemCount;
  const lastPageIndex = Math.max(Math.ceil(totalCount / viewItemCount) - 1, 0);
  const pageIndexes = Array.from({ length: viewItemCount })
    .map((_, index) => startPageIndex + index)
    .filter(pageIndex => pageIndex <= lastPageIndex);

  /* pageIndex가 변경 되었을 때, indicatorIndex를 계산 */
  const renewIndicatorIndex = pageIndex => {
    const newIndicatorIndex = $utils.getIndicatorIndex(
      pageIndex,
      viewPageCount,
    );

    setIndicatorIndex(newIndicatorIndex);
  };

  /* 현재 pageIndex(currentIndex)를 이동시키는 함수 */
  const moveToPage = (index, isInit) => {
    const newPageIndex = Math.min(Math.max(index, 0), lastPageIndex);

    setCurrentIndex(newPageIndex);
    renewIndicatorIndex(newPageIndex);

    if (!isInit && typeof onChange === 'function') onChange(newPageIndex);
  };

  /* 부모의 pageIndex 변경되면 페이지 다시 세팅 */
  useEffect(() => {
    moveToPage(pageIndex, true);
  }, [pageIndex]);

  /* 페이지 번호 초기화 1페이지에 class 적용 'on' */
  useEffect(() => {
    if (initial) {
      setCurrentIndex(0);
    }
  }, [initial]);

  return (
    <div className="paging">
      {prevButtons.includes('all') && (
        <button className="btn-first" onClick={() => moveToPage(0)}>
          <span className="hidden">첫 페이지</span>
        </button>
      )}

      {prevButtons.includes('list') && (
        <button
          className="btn-pre2"
          onClick={() => moveToPage(currentIndex - viewPageCount)}
        >
          <span className="hidden">이전 목록</span>
        </button>
      )}

      {prevButtons.includes('page') && (
        <button
          className="btn-pre"
          onClick={() => moveToPage(currentIndex - 1)}
        >
          <span className="hidden">이전 페이지</span>
        </button>
      )}

      {pageIndexes.map(index => (
        <a
          key={index}
          href="#"
          className={index === currentIndex ? 'on' : ''}
          onClick={() => moveToPage(index)}
        >
          {index + 1}
        </a>
      ))}

      {nextButtons.includes('all') && (
        <button
          className="btn-next"
          onClick={() => moveToPage(currentIndex + 1)}
        >
          <span className="hidden">다음 페이지</span>
        </button>
      )}

      {nextButtons.includes('list') && (
        <button
          className="btn-next2"
          onClick={() => moveToPage(currentIndex + viewPageCount)}
        >
          <span className="hidden">다음 목록</span>
        </button>
      )}

      {nextButtons.includes('page') && (
        <button className="btn-last" onClick={() => moveToPage(lastPageIndex)}>
          <span className="hidden">마지막 페이지</span>
        </button>
      )}
    </div>
  );
};

SatPagination.propTypes = {
  pageIndex: $propTypes.number,
  totalCount: $propTypes.number,
  viewItemCount: $propTypes.number,
  viewPageCount: $propTypes.number,
  prevButtons: $propTypes.array,
  nextButtons: $propTypes.array,
  onChange: $propTypes.func,
};

SatPagination.defaultProps = {
  pageIndex: 0,
  viewItemCount: 10,
  viewPageCount: 10,
  prevButtons: ['all', 'list', 'page'],
  nextButtons: ['all', 'list', 'page'],
};

export default SatPagination;
